/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {ChangeEvent, memo, useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import Add from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import cloneDeep from 'lodash.clonedeep';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {Device, Devices} from '@ion/core/src/types/entities';
import {TertiaryIconButton} from '@ion/dls/src/components/buttons/TertiaryIconButton';
import {SearchField} from '@ion/dls/src/components/search/SearchField';
import TabView from '@ion/dls/src/components/tabs/TabView';
import {getUniqItems} from '@ion/dls/src/editors/ListEditorUtils';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import BulkViewDeviceGrid from './BulkViewDeviceGrid';
import {MAX_VIEW_DEVICES} from './BulkViewDevicesDialog';
import {
  setBulkViewSearch,
  setDevicesTabs,
  setSelectedDevicesTab,
  showBulkViewDevices,
  showBulkViewGrid
} from './state';
import {viewDevicesGridSearchTextSelector, viewDevicesGridShowDialogSelector, viewDevicesSelectedSelector} from './state-selectors';
import useBulkViewDeviceTabs from './useBulkViewDeviceTabs';
import useDeviceSmartSearch from './useDeviceSmartSearch';
import {ViewDeviceEditorProps} from './ViewDeviceEditor';
import {getDeviceIdentifier} from './ViewDeviceEditorUtils';
import ViewDeviceTopMenu, {ViewDeviceTopMenuProps} from './ViewDeviceTopMenu';

export interface BulkViewDeviceTabProps extends ViewDeviceTopMenuProps,
  Pick<ViewDeviceEditorProps, 'devicesPermissions' | 'hasCreateJob' | 'includeEditAction'> {
  populatedDevice?: Device | Devices;
  accountId: number;
}

// defines the styles used by BulkViewDeviceTabs component ...
export const useStyles = makeStyles((theme: DLSTheme) => ({
  root : {
    width         : '80%',
    height        : '50px',
    display       : 'flex',
    flex          : '1',
    flexDirection : 'row',
    alignItems    : 'center',
    overflow      : 'auto',
    margin        : '0 20px'
  },
  tabContainer : {
    width        : 'auto',
    borderBottom : `1px solid ${dlsColor('grey5')}`
  },
  flexStyle : {
    display : 'flex'
  },
  searchField : {
    width                          : '90%',
    [theme.breakpoints.down('sm')] : {
      marginRight : '0px',
      width       : '100%'
    }
  },
  endAdornment : {
    display              : 'flex',
    padding              : '0 10px',
    cursor               : 'pointer',
    '& .MuiSvgIcon-root' : {
      fontSize : 16
    }
  },
  errorMessage : {
    color      : dlsColor('caRed'),
    fontSize   : '14px',
    fontFamily : 'Roboto',
    fontWeight : 400,
    paddingTop : '5px'
  },
  topIcons : {
    marginTop : '7px'
  }
}));

// defines the override style for TabView component...
const tabOverridesStyle = () => ({
  backgroundStyle : {
    minWidth  : '150px !important',
    minHeight : 'inherit',
    fontSize  : '14px'
  },
  endAdornment : {
    '& .MuiSvgIcon-root' : {
      width  : '0.65em',
      height : '0.65em'
    }
  },
  customTabIndicator : {
    backgroundColor : dlsColor('black'),
    height          : '3px'
  },
  tabRoot : {
    minHeight : 'inherit'
  }
});

// styles that get passed to the SearchField component
const searchFieldStyles = () => ({
  searchFieldRoot : {
    height : '45px'
  },
  searchFieldInput : {
    fontSize : '16px'
  }
});

export const getDeviceTab = (device: Device, deviceLength: number) => {
  return {
    tab       : deviceLength,
    tabHeader : getDeviceIdentifier(device),
    data      : device
  };
};

export const getViewDeviceTabs = (selectedDevices) => {
  return selectedDevices?.map((device, index) => {
    return getDeviceTab(device, index);
  });
};

/**
 * Displays the tabs for a selected devices.
 *
 * ```typescript
 *
 *  import BulkViewDeviceTabs from './BulkViewDeviceTabs';
 *
 *  <BulkViewDeviceTabs selectedDevices={selectedDevices} />
 *
 * ```
 */
export const BulkViewDeviceTabs = ({
  onRefresh,
  onEdit,
  onSwap,
  onAudit,
  onCreateJob,
  onMoveDevice,
  populatedDevice,
  devicesPermissions,
  hasCreateJob,
  accountId,
  includeEditAction = false,
  hasFilePermissions = false
}: BulkViewDeviceTabProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [showSearch, setShowSearch]   = useState<boolean>(false);
  const [searchError, setSearchError] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState('');
  const selectedDevices               = useSelector(viewDevicesSelectedSelector);
  const showDeviceGrid                = useSelector(viewDevicesGridShowDialogSelector);
  const gridSearchText                = useSelector(viewDevicesGridSearchTextSelector);
  const {loadDevices}                 = useDeviceSmartSearch();
  const {
    tabItems,
    selectedIndex,
    handleOnChange,
    onCloseTab,
    getIdentifierExists,
    handleAddRDeviceTab,
    getMaxDeviceMessage,
    getDeviceExistMessage
  } = useBulkViewDeviceTabs();

  const getDeviceFromIdentifiers = useCallback((searchKeyword: string) => {
    try {
      loadDevices({
        includeSubGroups : true,
        searchKeyword    : searchKeyword?.trim(),
        accountId
      }, {
        onSuccess : (results) => {
          const resultsCount = results?.totalCount;
          if (resultsCount > 0) {
            if (resultsCount === 1) {
              const deviceObj = results?.devices?.[0];
              const deviceId = deviceObj?.id;
              const existTab = getIdentifierExists(deviceId);
              if (!isEmpty(existTab)) {
                dispatch(setSelectedDevicesTab(existTab?.data));
                getDeviceExistMessage(deviceId);
              } else {
                onSearchSuccess(deviceObj);
              }
              removeSearch();
            } else {
              setSearchError(false);
              dispatch(showBulkViewGrid(searchValue));
            }
          } else {
            setSearchError(true);
          }
        },
        onError : () => {
          setSearchError(true);
        }
      });
    } catch (err) {
      setSearchError(true);
    }
  }, [accountId, searchValue]);

  function handleDeviceSearch() {
    getDeviceFromIdentifiers(searchValue);
  }

  function removeSearch() {
    setSearchValue('');
    setSearchError(false);
    setShowSearch(false);
    dispatch(showBulkViewGrid('', false));
  }

  function onSearchChange(e: ChangeEvent<{name?: string; value: string;}>) {
    e.persist();
    const value = e.target.value;
    setSearchValue(value);
    dispatch(setBulkViewSearch(value?.trim()));
  }

  const onSearchSuccess = useCallback((device: Device) => {
    if (tabItems?.length >= MAX_VIEW_DEVICES) {
      getMaxDeviceMessage();
    } else {
      handleAddRDeviceTab(device);
      setShowSearch(false);
      setSearchError(false);
    }
  }, [tabItems]);

  const onDevicesSelected = useCallback((devices: Devices) => {
    const selected = tabItems?.map(({data}) => data);
    const selectedItems = [...selected, ...devices];
    const uniqItems     = getUniqItems(selectedItems);
    let displayDevices  = cloneDeep(uniqItems);
    setShowSearch(false);
    if (uniqItems?.length > MAX_VIEW_DEVICES) {
      displayDevices = uniqItems.slice(0, MAX_VIEW_DEVICES);
      getMaxDeviceMessage();
    }
    const items = getViewDeviceTabs(displayDevices);
    const len = displayDevices?.length;
    const selectedTabData = displayDevices?.[len-1];
    dispatch(showBulkViewDevices(displayDevices));
    dispatch(setDevicesTabs(items, selectedTabData));
    dispatch(setSelectedDevicesTab(selectedTabData));
  }, [tabItems]);

  const toggleSearch = useCallback(() => {
    if (showSearch) {
      setSearchValue('');
      dispatch(setBulkViewSearch(''));
    }
    setShowSearch(!showSearch);
  }, [showSearch]);

  useEffect(() => {
    if (selectedDevices?.length > MAX_VIEW_DEVICES) {
      getMaxDeviceMessage();
    }
  }, [selectedDevices]);

  useEffect(() => {
    const searchText = searchValue?.trim();
    if (!searchText) {
      dispatch(showBulkViewGrid('', false));
    } else if (searchText?.length > 2) {
      getDeviceFromIdentifiers(searchValue);
    }
  }, [searchValue]);

  return (
    <>
      <div className={classes.flexStyle}>
        <div className={classes.root}>
          <div className={classes.tabContainer}>
            <TabView customTabs={tabItems.length > 1}
              closeForFirstTab
              centered={false}
              variant={'standard'}
              overrides={tabOverridesStyle}
              tabItems={tabItems}
              onChange={handleOnChange}
              onClose={onCloseTab}
              value={selectedIndex || 0} />
          </div>
        </div>
        <div>
          {showSearch ? (
            <div className={classes.searchField}>
              <SearchField overrides={searchFieldStyles}
                placeholder={searchError ? translate('admin-devices:dialogBulkView.noResults') : translate('admin-devices:dialogBulkView.addDevice')}
                onChange={onSearchChange}
                optionsProps={{
                  detailedDeviceGridSearch : true,
                  onKeyPress               : handleDeviceSearch,
                  value                    : searchValue
                }}
                endAdornment={searchValue
                  ? (
                    <div className={classes.endAdornment}
                      onClick={removeSearch}>
                      <CloseIcon />
                    </div>
                  ) : null} />
              {searchError && (
                <div className={classes.errorMessage}>{translate('admin-devices:dialogBulkView.noResults')}</div>
              )}
            </div>) : (
            <TertiaryIconButton iconDirection='left'
              disabled={tabItems?.length >= MAX_VIEW_DEVICES}
              label={''}
              icon={<Add style={{color: dlsColor('grey3')}} />}
              onClick={toggleSearch} />
          )}
        </div>
        <div className={classes.topIcons}>
          <ViewDeviceTopMenu onRefresh={onRefresh}
            onEdit={onEdit}
            onSwap={onSwap}
            onAudit={onAudit}
            onCreateJob={onCreateJob}
            onMoveDevice={onMoveDevice}
            devicesPermissions={devicesPermissions}
            selectedDevice={populatedDevice}
            hasFilePermissions={hasFilePermissions}
            hasCreateJob={hasCreateJob}
            includeEditAction={includeEditAction} />
        </div>
      </div>
      {showDeviceGrid && gridSearchText && (
        <BulkViewDeviceGrid accountId={accountId}
          searchValue={searchValue}
          onDevicesSelected={onDevicesSelected}
          onSearchSuccess={onSearchSuccess}
          removeSearch={removeSearch} />
      )}
    </>
  );
};

export default memo(BulkViewDeviceTabs);
